import React from 'react';
import {
    faUsersCog,
    faInfoCircle,
    faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FormGroup, Input, Alert } from 'reactstrap';
import { BaseFormViewModel } from '../common/ViewModel';
import {
    FormCheckbox,
    FormLabel,
    onFieldChange,
    toasty,
    ValidationErrorMessage,
} from '../common/forms/FormElements';
import CommonContext from '../Common';
import { ApiRoutes } from '../ApiRoutes';
import SlideForm from '../common/forms/SlideForm';
import { util } from '../Util';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { Crew } from './Crew';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { HexColorPicker } from 'react-colorful';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { v4 as uuidv4 } from 'uuid';
import LtsJobCard from '../longTermSchedule/LtsJobCard';

export default class CrewForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.crewForm = React.createRef();

        const stateBase = {
            crew: new Crew(),
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.resetForm = this.resetForm.bind(this);
        this.isNew = this.isNew.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onChange = onFieldChange;

    handleSaveError = (err) => handleFormSaveError(this, err);

    onClose = () => {
        this.resetForm();
        this.props.toggleShow(false);
        this.context.setFormOpened(false);
        this.props.onClose();
    };

    onDelete = async (e) => {
        const { crew } = this.state;

        try {
            const response = await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.crew.delete(crew.id),
                { crewId: crew.id }
            );

            if (response && response.crewId) {
                toast.success(`${crew.name} has been soft deleted.`);
                this.onClose();
            }
        } catch (e) {
            console.error({ e });
        }
    };

    onSubmit = async (e) => {
        const { crew } = this.state;

        this.setState({ errors: {}, saving: true });

        const url = this.isNew()
            ? ApiRoutes.crew.create()
            : ApiRoutes.crew.update();

        const response = await util.fetch
            .andGetResponse(util.fetch.types.post, url, crew)
            .catch(this.handleSaveError);

        if (response) {
            this.onClose();
            toasty.success('Crew Saved', 'Crew saved successfully.');
        }
    };

    isNew = () => {
        const { crew } = this.state;

        return Boolean(!crew || !crew.id || crew.id <= 0);
    };

    openCrew = async (crewId) => {
        await this.setState({ loading: true, crew: new Crew() });

        await this.populateState();

        if (crewId > 0) {
            const crew = await util.fetch.js(ApiRoutes.crew.get(crewId));

            this.setState({ crew });
        }

        this.setState({
            loading: false,
        });

        this.props.toggleShow(true);
        this.context.setFormOpened(true);
    };

    async populateState() {}

    resetForm = () => {
        this.setState({
            formValidated: false,
            crew: new Crew(),
        });
    };

    getAssociatedEquipmentIds = (equipmentId) => {
        const equipmentIds = [];
        if (!equipmentId) {
            return equipmentIds;
        }

        const { equipment } = this.props;

        let relatedEquipments = equipment.filter(
            (aeq) => aeq.equipmentAssignedId == equipmentId
        );

        relatedEquipments.forEach((req) => {
            equipmentIds.push(req.id);
            const attachedEquipment = equipment.find(
                (aeq) => aeq.id == req.equipmentAssignedId
            );

            if (attachedEquipment) {
                equipmentIds.push(attachedEquipment.id);
            }
        });

        return equipmentIds;
    };

    onEmployeeChange = (selectedEmployees) => {
        const { crew } = this.state;

        // Isolate added employees by doing a diff
        const crewMembers = new Set(crew.crewEmployeeMemberships);
        const newEmployee = selectedEmployees.filter(
            (emp) => !crewMembers.has(emp.id)
        )[0];

        if (newEmployee && newEmployee.equipment) {
            newEmployee.equipment.forEach((empeq) => {
                if (!crew.crewEquipmentMemberships.includes(empeq.id)) {
                    crew.crewEquipmentMemberships.push(empeq.id);
                }

                const equipmentIds = this.getAssociatedEquipmentIds(empeq.id);

                equipmentIds.forEach((eqid) => {
                    if (crew.crewEquipmentMemberships.includes(eqid)) {
                        return;
                    }

                    crew.crewEquipmentMemberships.push(eqid);
                });
            });
        }

        crew.crewEmployeeMemberships = selectedEmployees.map((e) => e.id);

        if (!crew.crewEmployeeMemberships.includes(crew.crewLeadEmployeeId)) {
            crew.crewLeadEmployeeId = null;
        }

        this.setState({ crew });
    };

    onEquipmentChange = (selectedEquipments) => {
        const { crew } = this.state;

        // Related employee or equipment?
        // Isolate added equipment by doing a diff
        const eqMembers = new Set(crew.crewEquipmentMemberships);
        const newEquipments = selectedEquipments.filter(
            (eq) => !eqMembers.has(eq.id)
        );

        crew.crewEquipmentMemberships = selectedEquipments.map((e) => e.id);

        newEquipments.forEach((newEquipment) => {
            // If there was an add, see if this equipment has an employee assigned
            // and add the employee
            if (newEquipment?.employeeAssignedId > 0) {
                crew.crewEmployeeMemberships.push(
                    newEquipment.employeeAssignedId
                );
            }

            const equipmentIds = this.getAssociatedEquipmentIds(
                newEquipment.id
            );

            equipmentIds.forEach((newEqId) => {
                if (crew.crewEquipmentMemberships.includes(newEqId)) {
                    return;
                }

                crew.crewEquipmentMemberships.push(newEqId);
            });
        });

        this.setState({ crew });
    };

    onDivisionChange = (selectedDivision) => {
        const { crew } = this.state;

        crew.divisionId = selectedDivision.id;

        this.setState(crew);
    };

    onColorChange = (colorHex) => {
        const { crew } = this.state;

        crew.colorHex = colorHex;

        this.setState({ crew });
    };

    onCrewLeadEmployeeChange = (crewLeadEmployee) => {
        const { crew } = this.state;

        crew.crewLeadEmployeeId = crewLeadEmployee ? crewLeadEmployee.id : null;

        this.setState({ crew });
    };

    getEquipmentDescription = (equipment) =>
        !equipment
            ? null
            : `${equipment.identifier} - ${equipment.description}`;

    generateAssignmentAlerts = () => {
        const { crew } = this.state;
        const { employees, equipment } = this.props;

        let assignments = [];

        // Employee assignments
        for (let i = 0; i < crew.crewEmployeeMemberships.length; i++) {
            const memId = crew.crewEmployeeMemberships[i];
            const employee = employees.find((e) => e.id == memId);

            if (!employee) {
                continue;
            }

            if (employee.equipment?.length > 0) {
                const isMapped = crew.crewEquipmentMemberships.find(
                    (m) => m == employee.equipment[0].id
                );

                if (isMapped) {
                    assignments.push(
                        `${employee.lastName}, ${
                            employee.firstName
                        } --> ${this.getEquipmentDescription(
                            employee.equipment[0]
                        )}`
                    );
                }
            }
        }

        // Equipment assignments
        for (let c = 0; c < crew.crewEquipmentMemberships.length; c++) {
            const memId = crew.crewEquipmentMemberships[c];
            const eq = equipment.find((e) => e.id == memId);

            if (
                eq?.equipmentAssignedId > 0 &&
                eq.id != eq?.equipmentAssignedId
            ) {
                const isMapped = crew.crewEquipmentMemberships.find(
                    (m) => m == eq?.equipmentAssignedId
                );

                if (isMapped) {
                    const eq2 = equipment.find(
                        (e) => e.id == eq.equipmentAssignedId
                    );
                    assignments.push(
                        `${this.getEquipmentDescription(
                            eq
                        )} --> ${this.getEquipmentDescription(eq2)}`
                    );
                }
            }
        }

        return assignments;
    };

    isEmployeeEligibleOrAlreadyPartOfThisCrew = (employee, crew) =>
        Boolean(employee);
    // bugbug: filtering disabled per TFS issue 5113
    // (employee &&
    //     employee.accountStatusId == 1 &&
    //     (!employee.crewId || employee.crewId == crew.id)) ||
    // crew.crewEmployeeMemberships.includes(employee.id);

    isEquipmentEligibleOrAlreadyPartOfThisCrew = (equipment, crew) =>
        Boolean(equipment);
    // (equipment &&
    //     equipment.equipmentStatusId == 1 &&
    //     (!equipment.crewId || equipment.crewId == crew.id)) ||
    // crew.crewEquipmentMemberships.includes(equipment.id);

    getOptionEmployeeDescription = (employee, crew) => {
        const inactive = employee.accountStatusId == 2 ? ' (Inactive)' : '';

        const partOfAnotherCrew =
            employee.crewId && employee.crewId != crew.id
                ? ' [Part of another Crew]'
                : '';

        return `${employee.lastName}, ${employee.firstName} (${employee.userName})${inactive}${partOfAnotherCrew}`;
    };

    getOptionEquipmentDescription = (equipment, crew) => {
        const inactive = equipment.equipmentStatusId == 2 ? ' (Inactive)' : '';

        const partOfAnotherCrew =
            equipment.crewId && equipment.crewId != crew.id
                ? ' [Part of another Crew]'
                : '';
        return `${equipment.identifier} - ${equipment.description}${inactive}${partOfAnotherCrew}`;
    };

    render() {
        const { formValidated, validationMessage, crew, loading, errors } =
            this.state;

        const { show, employees, equipment, divisions } = this.props;

        if (loading) {
            return null;
        }

        const division = divisions.find((d) => d.id == crew.divisionId) || {};
        const assignments = this.generateAssignmentAlerts();

        return (
            <SlideForm
                loading={loading}
                show={show}
                id="crewForm"
                formIcon={faUsersCog}
                formTitle={this.isNew() ? 'Add Crew' : 'Edit Crew'}
                ref={this.formRef}
                setIsValidated={(formValidated) =>
                    this.setState({ formValidated })
                }
                isValidated={formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onSave={this.onSave}
                onDelete={this.onDelete}
                errors={errors}
                onClearErrors={this.onClearErrors}
                validationMessage={validationMessage}
                entityId={crew.id}
            >
                <FormGroup>
                    <FormLabel
                        htmlFor="crew.name"
                        text="Name"
                        required={true}
                    />
                    <Input
                        id="crew.name"
                        name="crew.name"
                        value={crew.name ?? ''}
                        onChange={this.onChange}
                        placeholder="Crew Name"
                        type="text"
                        max={255}
                        maxLength={255}
                        required={true}
                        minLength={1}
                    />
                    <ValidationErrorMessage />
                </FormGroup>

                <FormGroup>
                    <FormLabel
                        htmlFor="crew.divisionId"
                        text="Division"
                        required={true}
                    />
                    <ValidatedSelect
                        id="crew.divisionId"
                        name="crew.divisionId"
                        options={divisions.filter(
                            (d) => !d.inactive || crew.divisionId == d.id
                        )}
                        getOptionValue={(d) => d.id}
                        getOptionLabel={(d) =>
                            !d.inactive ? d.name : `${d.name} (Inactive)`
                        }
                        value={
                            divisions.filter((d) => crew.divisionId == d.id) ??
                            ''
                        }
                        onChange={this.onDivisionChange}
                    />
                </FormGroup>

                {Boolean(!crew.colorHex || crew.colorHex == '#ffffff') && (
                    <Alert color="warning" fade={false}>
                        <FontAwesomeIcon
                            icon={faExclamationCircle}
                            className="mr-2"
                        />
                        Please make sure to set the crew's card background
                        color.
                    </Alert>
                )}

                <FormGroup>
                    <FormLabel
                        htmlFor="crew.crewEmployeeMemberships"
                        text="Employees"
                    />
                    <ValidatedSelect
                        id="crew.crewEmployeeMemberships"
                        name="crew.crewEmployeeMemberships"
                        isMulti={true}
                        options={employees.filter((e) =>
                            this.isEmployeeEligibleOrAlreadyPartOfThisCrew(
                                e,
                                crew
                            )
                        )}
                        getOptionValue={(e) => e.id}
                        getOptionLabel={(e) =>
                            this.getOptionEmployeeDescription(e, crew)
                        }
                        value={
                            employees.filter((e) =>
                                crew.crewEmployeeMemberships.includes(e.id)
                            ) ?? ''
                        }
                        onChange={this.onEmployeeChange}
                    />
                </FormGroup>

                {crew.crewEmployeeMemberships.length > 0 && (
                    <FormGroup>
                        <FormLabel
                            htmlFor="crew.crewLeadEmployeeId"
                            text="Crew Lead"
                        />
                        <ValidatedSelect
                            id="crew.crewLeadEmployeeId"
                            name="crew.crewLeadEmployeeId"
                            options={employees.filter((e) =>
                                crew.crewEmployeeMemberships.includes(e.id)
                            )}
                            getOptionValue={(e) => e.id}
                            getOptionLabel={(e) => {
                                const inactive =
                                    e.accountStatusId == 2 ? ' (Inactive)' : '';
                                return `${e.lastName}, ${e.firstName} (${e.userName})${inactive}`;
                            }}
                            value={
                                employees.find(
                                    (e) => crew.crewLeadEmployeeId == e.id
                                ) ?? ''
                            }
                            onChange={this.onCrewLeadEmployeeChange}
                        />
                    </FormGroup>
                )}

                <FormGroup>
                    <FormLabel
                        htmlFor="crew.crewEquipmentMemberships"
                        text="Equipment"
                    />
                    <ValidatedSelect
                        id="crew.crewEquipmentMemberships"
                        name="crew.crewEquipmentMemberships"
                        isMulti={true}
                        options={equipment.filter((eq) =>
                            this.isEquipmentEligibleOrAlreadyPartOfThisCrew(
                                eq,
                                crew
                            )
                        )}
                        getOptionValue={(eq) => eq.id}
                        getOptionLabel={(eq) =>
                            this.getOptionEquipmentDescription(eq, crew)
                        }
                        value={
                            equipment.filter((eq) =>
                                crew.crewEquipmentMemberships.includes(eq.id)
                            ) ?? ''
                        }
                        onChange={this.onEquipmentChange}
                    />
                </FormGroup>

                {assignments.length > 0 && (
                    <>
                        <FormLabel text="Required Assignments" />

                        <Alert
                            color="info"
                            fade={false}
                            style={{ padding: '7px' }}
                        >
                            {assignments.map((a) => (
                                <div key={uuidv4()} style={{ margin: '3px' }}>
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className="mr-2"
                                    />
                                    {a}
                                </div>
                            ))}
                        </Alert>
                    </>
                )}

                <FormGroup>
                    <FormLabel
                        htmlFor="crew.colorHex"
                        text="Color"
                        required={true}
                    />
                    <HexColorPicker
                        color={crew.colorHex}
                        onChange={this.onColorChange}
                        className="mb-3"
                    />

                    <LtsJobCard
                        event={{
                            text: `${crew.name} (Unpublished Preview)`,
                            crewColorHex: crew.colorHex,
                            divisionColorHex: division.colorHex,
                        }}
                    />

                    <LtsJobCard
                        event={{
                            text: `${crew.name} (Published Preview)`,
                            crewColorHex: crew.colorHex,
                            divisionColorHex: division.colorHex,
                            isPublished: true,
                        }}
                    />
                </FormGroup>

                <FormGroup>
                    <FormCheckbox
                        name="crew.isInactive"
                        checked={crew.isInactive}
                        onChange={this.onChange}
                        labelText="Inactive?"
                    />
                </FormGroup>
            </SlideForm>
        );
    }
}
